'use client';

// Third Party
import * as React from 'react';
import cx from 'classnames';

// UI
import { Typography } from '@components/typography';

export type QuickNavSection = {
  id: string;
  label: string;
};

type Props = {
  sections: QuickNavSection[];
  isVisible: boolean;
};

export const QuickNav: React.FC<Props> = ({ sections, isVisible }) => {
  const [activeSection, setActiveSection] = React.useState<string>('');

  React.useEffect(() => {
    const handleScroll = () => {
      // Find which section is currently at the top of the viewport
      const scrollPosition = window.scrollY + 150; // Offset for the nav bar

      // Check each section's position
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        const element = document.getElementById(section.id);

        if (element) {
          const { offsetTop } = element;

          // If we've scrolled past this section, it's the active one
          if (scrollPosition >= offsetTop) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    // Run on mount and scroll
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  const handleSectionClick = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      // For photos section, scroll to top of page
      if (sectionId === 'photos') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        // For other sections, use scrollIntoView
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <nav
      className="hidden md:block fixed top-0 left-0 w-full bg-white/90 backdrop-blur-sm border-b border-gray-light shadow-sm z-40"
      role="navigation"
      aria-label="Quick navigation"
    >
      <div className="body-container">
        <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide py-4">
          {sections.map((section) => {
            const isActive = activeSection === section.id;
            return (
              <button
                key={section.id}
                onClick={() => handleSectionClick(section.id)}
                className={cx(
                  'px-4 py-2 whitespace-nowrap transition-all duration-200 border-b-2 hover:text-primary-light rounded-sm',
                  isActive
                    ? 'border-primary-main text-primary-main font-medium'
                    : 'border-transparent text-gray-main font-normal'
                )}
                aria-current={isActive ? 'location' : undefined}
              >
                <Typography
                  variant="body2"
                  component="span"
                  className={cx(
                    'transition-colors',
                    isActive ? 'font-medium' : 'font-normal'
                  )}
                >
                  {section.label}
                </Typography>
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
};
